import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormBuilderPreviewTagModel } from '../../models/form-builder-preview-tag-model';
import { FormBuilderSectionModel } from '../../models/form-builder-section-model';
import { FormBuilderSeedModel } from '../../models/form-builder-seed-model';
import { DateValidator } from '../../validators/date-validator';

@Component({
  selector: 'tf-core-fb-preview',
  templateUrl: './fb-preview.component.html',
  styleUrls: ['./fb-preview.component.css']
})
export class FbCorePreviewComponent implements OnInit, OnDestroy {
    @Input() seed: FormBuilderSeedModel;
    @Input() formJson: string;
    @Input() readonly: boolean;

    @Output() autoSave: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
    autoSaveInterval;

    @Output() onSubmit: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();
    @Output() onClose: EventEmitter<null> = new EventEmitter<null>();

    public previewTags: FormBuilderPreviewTagModel[];

    public previewSections: FormBuilderSectionModel[] = [];

    public previewComponents: FormBuilderSectionModel[] = [];

    public hasSection: boolean = false;

    public selectedSection: string = '';

    public selectedPermission: string = '';

    public form: FormGroup;

    constructor(
    ) { }

    ngOnInit(): void {
        this.init();
        if(this.formJson) this.form.setValue(JSON.parse(this.formJson)) ;
        this.autoSaveInterval = setInterval(() => this.saveProgress(), 3000);
    }

    ngOnDestroy(): void {
        clearInterval(this.autoSaveInterval);
    }

    saveProgress(): void {
        if (this.form.dirty) {
            this.form.markAsPristine();
            this.autoSave.emit(this.form);
        }
    }

    submit(): void {
        this.onSubmit.emit(this.form);
    }

    close(): void {
        this.onClose.emit();
    }

    private init(): void {
        this.form = new FormGroup({});

        let currentSection = '';
        
        this.hasSection = this.seed?.sections[0]?.components[0]?.type === 'section';

        this.previewSections = [];

        this.previewComponents = [];

        this.seed.sections.map((section: FormBuilderSectionModel) => {
            if (section.components[0].type === 'section') {
                currentSection = section.label;

                if (this.readonly) {
                    section.setAsReadonly(true)
                }
                else if (this.selectedPermission.length > 0) {
                    for (let item of section.permissions) {
                        if (item.tag.toLowerCase() === this.selectedPermission.toLowerCase()) {
                            if (item.permission === 'not_visible') {
                                return;
                            }

                            section.setAsReadonly(true);
                        }
                    }
                }

                this.previewSections.push(section);

                if (this.selectedSection === '') {
                    this.selectedSection = section.label;
                }
                
                return;
            }

            if (currentSection !== this.selectedSection) {
                return;
            }

            if (this.readonly) {
                section.setAsReadonly(true)
            }
            else if (this.selectedPermission.length > 0) {
                for (let item of section.permissions) {
                    if (item.tag.toLowerCase() === this.selectedPermission.toLowerCase()) {
                        if (item.permission === 'not_visible') {
                            return;
                        }

                        section.setAsReadonly(true);
                    }
                }
            }

            this.previewComponents.push(section);
            this.addControl(section);
        });
    }

    private addControl(section: FormBuilderSectionModel): void {
        //const component = section.components[0];


        section.components.forEach(component => {

            const validators = [];

            const label = `${section.label}_${component.label}`;

            switch (component.type) {
                case 'text_input':
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }

                    if (component.subtype === 'multi_line' && component.characterCount > 0) {
                        validators.push(Validators.maxLength(component.characterCount));
                    }

                    this.form.addControl(label, new FormControl({ value: '', disabled: section.readonly }, validators));
                    break;
                case 'email_input':
                    validators.push(Validators.email);
                    
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }

                    this.form.addControl(label, new FormControl('', validators));
                    break;
                case 'number_input':
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }

                    this.form.addControl(label, new FormControl('', validators));
                    break;
                case 'date_input':
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }

                    if (component.validation === 'DDMMYYYY') {
                        validators.push(DateValidator.internationalDate);
                    }
                    
                    this.form.addControl(label, new FormControl('', validators));
                    break;
                case 'date_from_and_to':
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }

                    if (component.validation === 'DDMMYYYY') {
                        validators.push(DateValidator.internationalDate);
                    }
                    
                    this.form.addControl(label, new FormGroup({
                        from: new FormControl('', validators),
                        to: new FormControl('', validators)
                    }));

                    break;
                case 'single_select':
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }
                    
                    this.form.addControl(label, new FormControl('', validators));
                    break;
                case 'multi_select':
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }

                    // TODO: check how to validate if min/max qty are checked
                    
                    this.form.addControl(label, new FormControl('', validators));
                    break;
                case 'rate_input':
                    if (component.mandatory) {
                        validators.push(Validators.required);
                    }
                    
                    this.form.addControl(label, new FormControl('', validators));
                    break;
                case 'checklist':
                    // TODO: check if we need to validate checklist fields
                    break;
                default:
                    break;
            }
        });
    }

    public isFirstSectionSelected(): boolean {
        const previousIndex = (this.previewSections.findIndex(x => x.label === this.selectedSection) - 1);

        return !(previousIndex >= 0);
    }

    public isLastSectionSelected(): boolean {
        const nextIndex = (this.previewSections.findIndex(x => x.label === this.selectedSection) + 1);

        return !(nextIndex < this.previewSections.length);
    }
}
